import { motion } from "framer-motion";
import { MapPin, Mail, Phone, Clock, CheckCircle2, ExternalLink } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const GOOGLE_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSfpDKEQUBL-yj35bqC06YxF4gkNHLR60cqYXe7W7jxExDImAQ/viewform?embedded=true";

const GOOGLE_FORM_LINK =
  "https://docs.google.com/forms/d/e/1FAIpQLSfpDKEQUBL-yj35bqC06YxF4gkNHLR60cqYXe7W7jxExDImAQ/viewform";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { visible: { transition: { staggerChildren: 0.09 } } };

/* @section: contact */
export default function Contact() {
  const { t } = useLanguage();

  const contactInfo = [
    {
      icon: <MapPin className="w-5 h-5" />,
      labelEn: "Location",
      labelKo: "위치",
      valueEn: "San Jose, California (Bay Area)",
      valueKo: "캘리포니아 샌호세 (베이 에어리어)",
    },
    {
      icon: <Mail className="w-5 h-5" />,
      labelEn: "Email",
      labelKo: "이메일",
      valueEn: "nexbizmanagement@gmail.com",
      valueKo: "nexbizmanagement@gmail.com",
      href: "mailto:nexbizmanagement@gmail.com",
    },
    {
      icon: <Phone className="w-5 h-5" />,
      labelEn: "Phone",
      labelKo: "전화",
      valueEn: "(650) 309-9319",
      valueKo: "(650) 309-9319",
      href: "tel:+16503099319",
    },
    {
      icon: <Clock className="w-5 h-5" />,
      labelEn: "Office Hours",
      labelKo: "운영 시간",
      valueEn: "Mon–Fri: 9am–6pm PT",
      valueKo: "월–금: 오전 9시–오후 6시 (PT)",
    },
  ];

  return (
    <>
      {/* Hero */}
      <section className="bg-primary py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p
              variants={fadeUp}
              className="text-accent text-sm font-semibold uppercase tracking-wider mb-3"
            >
              {t("Contact Us", "문의하기")}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4"
            >
              {t(
                "Let's Make Your Tax Situation Clearer",
                "세금 문제, 더 명확하게 정리해드리겠습니다"
              )}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-primary-foreground/70 text-lg max-w-2xl mx-auto"
            >
              {t(
                "Schedule a consultation or send us your tax question. We respond in English and Korean.",
                "상담을 예약하거나 세금 질문을 보내주세요. 영어와 한국어로 답변드립니다."
              )}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">

            {/* Google Form Embed */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
              className="lg:col-span-3"
            >
              <motion.div variants={fadeUp} className="flex items-center justify-between mb-5">
                <h2 className="text-2xl font-bold text-foreground">
                  {t("Send Us a Message", "문의 메시지 보내기")}
                </h2>
                <a
                  href={GOOGLE_FORM_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  {t("Open in new tab", "새 탭으로 열기")}
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </motion.div>

              {/* iframe wrapper */}
              <motion.div
                variants={fadeUp}
                className="rounded-2xl overflow-hidden border border-border shadow-sm bg-white"
              >
                <iframe
                  src={GOOGLE_FORM_URL}
                  width="100%"
                  height="900"
                  style={{ border: "none" }}
                  title="NexBiz Tax Advisor Contact Form"
                  loading="lazy"
                >
                  {t(
                    "Loading form… if it doesn't appear, ",
                    "양식 로딩 중… 표시되지 않으면 "
                  )}
                  <a href={GOOGLE_FORM_LINK} target="_blank" rel="noopener noreferrer">
                    {t("click here to open it.", "여기를 클릭해 여세요.")}
                  </a>
                </iframe>
              </motion.div>

              {/* Disclaimer */}
              <motion.div
                variants={fadeUp}
                className="mt-4 p-4 bg-muted rounded-xl border border-border"
              >
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t(
                    "By submitting this form, you acknowledge that this contact does not create a client-advisor relationship. Information shared here is for inquiry purposes only. Engagement begins only after a signed agreement.",
                    "이 양식을 제출함으로써 이 연락이 고객-어드바이저 관계를 형성하지 않음을 인정합니다. 여기서 공유된 정보는 문의 목적으로만 사용됩니다. 공식 서비스는 계약서 서명 후에만 시작됩니다."
                  )}
                </p>
              </motion.div>
            </motion.div>

            {/* Contact Info Sidebar */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
              className="lg:col-span-2 space-y-6"
            >
              <motion.h2 variants={fadeUp} className="text-2xl font-bold text-foreground">
                {t("Contact Information", "연락처 정보")}
              </motion.h2>

              <div className="space-y-4">
                {contactInfo.map((info, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    className="flex items-start gap-4 p-4 bg-card border border-border rounded-xl"
                  >
                    <div className="w-9 h-9 rounded-lg bg-primary/8 flex items-center justify-center text-primary shrink-0">
                      {info.icon}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
                        {t(info.labelEn, info.labelKo)}
                      </p>
                      {info.href ? (
                        <a
                          href={info.href}
                          className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                        >
                          {t(info.valueEn, info.valueKo)}
                        </a>
                      ) : (
                        <p className="text-sm font-medium text-foreground">
                          {t(info.valueEn, info.valueKo)}
                        </p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Before Your Consultation */}
              <motion.div
                variants={fadeUp}
                className="p-5 bg-primary rounded-xl text-primary-foreground"
              >
                <h3 className="font-bold text-base mb-3">
                  {t("Before Your Consultation", "상담 전 안내")}
                </h3>
                <ul className="space-y-2.5">
                  {[
                    {
                      en: "Initial consultations are 30–45 minutes",
                      ko: "초기 상담은 30~45분 소요됩니다",
                    },
                    {
                      en: "Available via video call (Zoom) or phone",
                      ko: "화상 통화(Zoom) 또는 전화로 가능합니다",
                    },
                    {
                      en: "Please have recent tax returns ready if applicable",
                      ko: "해당 시 최근 세금보고서를 준비해주세요",
                    },
                    {
                      en: "We respond in English and Korean",
                      ko: "영어와 한국어로 답변드립니다",
                    },
                  ].map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2.5 text-sm text-primary-foreground/80"
                    >
                      <CheckCircle2 className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                      {t(item.en, item.ko)}
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Response time note */}
              <motion.div
                variants={fadeUp}
                className="p-4 bg-accent/10 border border-accent/20 rounded-xl"
              >
                <p className="text-sm text-foreground font-medium mb-1">
                  {t("⏱ Response Time", "⏱ 응답 시간")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "We typically respond within 1–2 business days. For urgent matters, please call us directly.",
                    "보통 영업일 기준 1~2일 내 응답드립니다. 긴급한 경우 직접 전화해 주세요."
                  )}
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
